<template>
  <layout-admin :nav-menu-items="navMenuItems">
    <slot v-if="!isLoading" />

    <!-- Slot: Navbar -->
    <template #navbar="{ toggleVerticalNavMenuActive }">
      <div class="navbar-content-container">
        <!-- Left Content: -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>

          <v-btn
            v-if="['staging', 'development'].includes(currentEnv)"
            color="error"
            :ripple="false"
            small
          >
            {{
              {
                staging: 'STG',
                development: 'DEV'
              }[currentEnv]
            }}
          </v-btn>
        </div>

        <!-- Right Content: -->
        <div class="d-flex align-center right-row">
          <clock class="mr-2" />
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            class="mr-2"
            @click="$router.go(0)"
          >
            <v-icon>
              {{ icons.mdiReload }}
            </v-icon>
          </v-btn>
          <app-bar-theme-switcher class="mr-2" />
          <monitor-menu class="mr-5" />
          <cash-register-menu />
          <div
            v-show="$route.name === 'admin-home' && $vuetify.breakpoint.mdAndDown"
            class="mr-11"
          >
          </div>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <!-- TODO -->
    </template>
  </layout-admin>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  getCurrentInstance,
  provide,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import { mdiMenu, mdiHeartOutline, mdiReload } from '@mdi/js'
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import CashRegisterMenu from '@core/layouts/components/app-bar/CashRegisterMenu.vue'
import MonitorMenu from '@core/layouts/components/app-bar/MonitorMenu.vue'
import Clock from '@/views/components/util/Clock.vue'
import navMenuItems from '@/navigation/admin'
import LayoutAdmin from '@/@core/layouts/variants/LayoutAdmin.vue'
import ClubApi from '@/api/waiter/Club'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import ReceiptNotePresetApi from '@/api/v2/utility/settings/ReceiptNotePreset'
import MonitorTimecardApi from '@/api/v2/monitor/Timecard'
import MonitorTableApi from '@/api/v2/monitor/Table'
import MonitorVendingHistoryApi from '@/api/v2/monitor/VendingHistory'
import MonitorKpiApi from '@/api/v2/monitor/Kpi'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import useCompInit from '@/views/composable/useCompInit'
import useAudioPlayer from '@/views/composable/useAudioPlayer'
import useKitchenPrint from '@/views/composable/useKitchenPrint'

export default {
  components: {
    LayoutAdmin,
    AppBarThemeSwitcher,
    Clock,
    CashRegisterMenu,
    MonitorMenu,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { isLoading, initWith } = useCompInit()
    const {
      isConnected,
      isConnecting,
      printer,
      checkConnection,
    } = useReceiptPrinterConnection()

    // AudioPlayer: iOS/iPadOS向け通知音アンロック機構
    const {
      setupUnlockListeners,
      preloadSounds,
      playSound,
    } = useAudioPlayer()

    const currentClubMeta = reactive({
      remainingCashRegisterMoney: 0,
      todaySalesUnfixed: 0,
      todaySales: 0,
      today: new Date().toISOString().substr(0, 10),
    })
    const receiptNotePresets = ref([])
    const customerTags = ref([])
    const currentStats = reactive({
      castCount: 0,
      tableCount: 0,
      customerCount: 0,
      castWorkingTime: 0,
      customerUsageTime: 0,
    })
    const kpiStats = reactive({
      customerCount: 0,
      salesAmount: 0,
      customKpis: [],
    })

    const getClub = async () => {
      const res = await ClubApi.getClub()

      if (res?.data?.data) {
        const {
          remainingCashRegisterMoney,
          todaySalesUnfixed,
          todaySales,
          today,
        } = res.data.data.meta

        currentClubMeta.remainingCashRegisterMoney = remainingCashRegisterMoney
        currentClubMeta.todaySalesUnfixed = todaySalesUnfixed
        currentClubMeta.todaySales = todaySales
        currentClubMeta.today = today
      }
    }

    const getReceiptNotePresets = async () => {
      try {
        const res = await ReceiptNotePresetApi.getReceiptNotePresets()
        receiptNotePresets.value = res.data.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const getActiveCastCount = async () => {
      try {
        const res = await MonitorTimecardApi.getActiveCastCount()
        currentStats.castCount = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getActiveTableCounts = async () => {
      try {
        const res = await MonitorTableApi.getActiveTableCounts()
        const { tableCount, customerCount } = res.data.data
        currentStats.tableCount = tableCount
        currentStats.customerCount = customerCount
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    // プラマイの情報を取得
    const fetchPlusMinusStats = async () => {
      await Promise.all([getActiveCastCount(), getActiveTableCounts()])
    }

    const getCastWorkingTime = async () => {
      try {
        const res = await MonitorTimecardApi.getCastWorkingTime()
        currentStats.castWorkingTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    const getCustomerUsageTime = async () => {
      try {
        const res = await MonitorVendingHistoryApi.getCustomerUsageTime()
        currentStats.customerUsageTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }
    // 稼働率の情報を取得
    const fetchOperationRateStats = async () => {
      await Promise.all([getCastWorkingTime(), getCustomerUsageTime()])
    }

    // KPI情報を取得
    const fetchKpiStats = async () => {
      try {
        const res = await MonitorKpiApi.getKpis()
        const { customer_count, sales_amount, custom_kpis } = res.data.data
        kpiStats.customerCount = customer_count
        kpiStats.salesAmount = sales_amount
        kpiStats.customKpis = custom_kpis
      } catch (error) {
        vm.$toast.error(error.response?.data?.message?.join('\n') || 'KPIの取得に失敗しました')
      }
    }

    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()
      if (res?.data) {
        customerTags.value = [...res.data.customerTags.data]
      }
    }

    checkConnection()
    initWith([
      getClub(),
      getReceiptNotePresets(),
      getCustomerTags(),
      fetchPlusMinusStats(),
      fetchOperationRateStats(),
      fetchKpiStats(),
    ])

    // キッチンプリンター印刷: モバイルオーダー注文時
    const { printKitchenSlips } = useKitchenPrint()
    const latestOrder = computed(() => vm.$store.state.selfOrder.latestOrder)

    watch(latestOrder, newOrder => {
      if (!newOrder || !newOrder.items || newOrder.items.length === 0) return

      printKitchenSlips({
        vendingHistories: newOrder.items.map(item => ({
          name: item.name,
          vendibleTagIds: item.vendible_tag_ids || [],
          remark: item.remark,
          recipients: item.recipients || [],
        })),
        staffName: newOrder.orderer_name || '',
        hallName: newOrder.table?.hall_name || '',
        tableCode: newOrder.table?.name || '',
        customerCount: newOrder.table?.customer_count || 0,
      })
    })

    // SelfOrderChannel接続 & AudioPlayer初期化
    onMounted(async () => {
      // iOS/iPadOS向けAudioPlayerアンロック機構を設定
      setupUnlockListeners()
      await preloadSounds()

      // audioPlayerをstoreに設定
      vm.$store.commit('selfOrder/SET_AUDIO_PLAYER', { playSound })

      // SelfOrderChannel接続
      vm.$store.dispatch('selfOrder/connect')
    })

    onBeforeUnmount(() => {
      vm.$store.dispatch('selfOrder/disconnect')
    })

    provide('currentClubMeta', currentClubMeta)
    provide('isReceiptPrinterConnected', isConnected)
    provide('isReceiptPrinterConnecting', isConnecting)
    provide('printer', printer)
    provide('getClub', getClub)
    provide('receiptNotePresets', receiptNotePresets)
    provide('customerTags', customerTags)
    provide('currentStats', currentStats)
    provide('fetchPlusMinusStats', fetchPlusMinusStats)
    provide('fetchOperationRateStats', fetchOperationRateStats)
    provide('kpiStats', kpiStats)
    provide('fetchKpiStats', fetchKpiStats)

    return {
      navMenuItems,
      currentClubMeta,
      isLoading,

      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiReload,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }
  }
}
</style>
